// TiptapEditor
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Heading from '@tiptap/extension-heading'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import { useEffect, useRef, useState } from 'react'
import { getApiUrl } from '../../api'
import { TextStyle } from '@tiptap/extension-text-style'
import { Extension } from '@tiptap/core'

interface Props {
  onChange?: (html: string) => void
  initialContent?: string
  imageUploadUrl?: string
  hasImage?: boolean
}

interface ImagePopup {
  el: HTMLImageElement
  x: number
  y: number
  size: string // 현재 적용된 크기 ('' = 미지정)
}

const IMAGE_SIZE_OPTIONS = ['25%', '50%', '75%', '100%']

// 이미지에 적용된 크기를 인라인 스타일에서 읽어온다 (선택 목록에 있는 값만 인정)
const readImageSize = (el: HTMLImageElement): string => {
  const value = el.style.width || el.style.maxWidth || ''
  return IMAGE_SIZE_OPTIONS.includes(value) ? value : ''
}

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style'),
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
    }
  },
})

const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: el => el.getAttribute('class'),
        renderHTML: attrs => attrs.class ? { class: attrs.class } : {},
      },
    }
  },
})

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] } },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize || null,
          renderHTML: attrs => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
        },
      },
    }]
  },
  addCommands() {
    return {
      setFontSize: (size: string) => ({ chain }: any) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }: any) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    } as any
  },
})

export default function TiptapEditor({ onChange, initialContent = '', imageUploadUrl, hasImage = true }: Props) {
  const [imagePopup, setImagePopup] = useState<ImagePopup | null>(null)
  const [currentFontSize, setCurrentFontSize] = useState('')
  const [, forceUpdate] = useState(0)
  const editorWrapRef = useRef<HTMLDivElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      TextStyle,
      CustomHeading.configure({ levels: [1, 2, 3] }),
      CustomImage.configure({ HTMLAttributes: { class: 'tiptap-img' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      FontSize,
    ],
    content: initialContent,
    editorProps: {
      attributes: { class: 'min-h-[400px] p-3 focus:outline-none' },
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML())
      setCurrentFontSize(editor.getAttributes('textStyle').fontSize ?? '')
    },
    onSelectionUpdate({ editor }) {
      setCurrentFontSize(editor.getAttributes('textStyle').fontSize ?? '')
      forceUpdate(n => n + 1)
    },
  })

  useEffect(() => {
    const wrap = editorWrapRef.current
    if (!wrap) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'IMG') {
        const img = target as HTMLImageElement
        const rect = img.getBoundingClientRect()
        const wrapRect = wrap.getBoundingClientRect()
        setImagePopup({
          el: img,
          x: rect.left - wrapRect.left + rect.width / 2,
          y: rect.top - wrapRect.top + 50,
          size: readImageSize(img),
        })
      } else {
        setImagePopup(null)
      }
    }
    wrap.addEventListener('click', handleClick)
    return () => wrap.removeEventListener('click', handleClick)
  }, [editor])

  const setImageAlign = (align: 'left' | 'center' | 'right' | 'float-left' | 'float-right') => {
    if (!imagePopup || !editor) return
    let layoutStyle = ''
    let defaultMaxWidth = '60%'
    if (align === 'left') layoutStyle = 'display:block !important; margin-left:0 !important; margin-right:auto !important;'
    if (align === 'center') layoutStyle = 'display:block !important; margin-left:auto !important; margin-right:auto !important;'
    if (align === 'right') layoutStyle = 'display:block !important; margin-left:auto !important; margin-right:0 !important;'
    if (align === 'float-left') { layoutStyle = 'float:left; margin-right:32px; margin-bottom:8px;'; defaultMaxWidth = '45%' }
    if (align === 'float-right') { layoutStyle = 'float:right; margin-left:32px; margin-bottom:8px;'; defaultMaxWidth = '45%' }

    // 정렬을 바꿔도 지정해 둔 크기는 유지한다
    const marginStyle = imagePopup.size
      ? `${layoutStyle} width:${imagePopup.size}; max-width:${imagePopup.size};`
      : `${layoutStyle} max-width:${defaultMaxWidth};`
    let targetPos = -1
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs.src === imagePopup.el.src) {
        targetPos = pos
        return false
      }
    })
    if (targetPos !== -1) {
      const { tr } = editor.state
      const node = editor.state.doc.nodeAt(targetPos)
      if (node) {
        tr.setNodeMarkup(targetPos, undefined, { ...node.attrs, style: marginStyle })
        editor.view.dispatch(tr)
      }
    }
    setImagePopup(null)
    onChange?.(editor.getHTML())
  }

  const setImageSize = (size: string) => {
    if (!imagePopup || !editor) return
    let targetPos = -1
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs.src === imagePopup.el.src) {
        targetPos = pos
        return false
      }
    })
    if (targetPos !== -1) {
      const { tr } = editor.state
      const node = editor.state.doc.nodeAt(targetPos)
      if (node) {
        const currentStyle = node.attrs.style ?? ''
        // max-width만 주면 원본보다 크게는 못 키우므로 width도 함께 지정한다
        const newStyle = currentStyle
          .replace(/max-width:[^;]+;?/g, '')
          .replace(/width:[^;]+;?/g, '')
          + ` width:${size}; max-width:${size};`
        tr.setNodeMarkup(targetPos, undefined, { ...node.attrs, style: newStyle.trim() })
        editor.view.dispatch(tr)
      }
    }
    setImagePopup(prev => (prev ? { ...prev, size } : prev))
    onChange?.(editor.getHTML())
  }


  const toggleTitleLine = () => {
    if (!editor) return
    const isActive = editor.isActive('heading', { class: 'title-line' })
    editor.chain().focus().updateAttributes('heading', { class: isActive ? null : 'title-line' }).run()
  }

  const handleImageUpload = async () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      try {
        const token = localStorage.getItem('access_token')
        const uploadUrl = imageUploadUrl ?? '/marubit/api/event/admin/upload-image'
        const res = await fetch(getApiUrl(uploadUrl), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })
        if (!res.ok) throw new Error('업로드 실패')
        const data = await res.json()
        editor?.chain().focus().setImage({ src: data.url }).run()
      } catch {
        alert('이미지 업로드에 실패했습니다.')
      }
    }
  }

  const btn = 'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors'
  const btnActive = 'px-3 py-1 text-sm border border-[#887267] rounded bg-[#f5f2f0] text-[#887267] transition-colors'

  return (
    <div style={{ position: 'relative' }}>
      {/* 툴바 */}
      <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-t-[10px] bg-gray-50">

        {/* 서식 */}
        <select
          className="px-2 py-1 text-sm border border-gray-300 rounded bg-white cursor-pointer"
          value={currentFontSize}
          onChange={(e) => {
            if (e.target.value === '') {
              (editor?.chain().focus() as any).unsetFontSize().run()
            } else {
              (editor?.chain().focus() as any).setFontSize(e.target.value).run()
            }
          }}
        >
          <option value="">크기</option>
          <option value="12px">12</option>
          <option value="14px">14</option>
          <option value="16px">16</option>
          <option value="18px">18</option>
          <option value="20px">20</option>
          <option value="24px">24</option>
          <option value="28px">28</option>
          <option value="32px">32</option>
        </select>
        <button className={btn} onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()} title="서식 초기화">T</button>
        <button className={editor?.isActive('bold') ? btnActive : btn} onClick={() => editor?.chain().focus().toggleBold().run()} title="굵게"><b>B</b></button>
        <button className={editor?.isActive('italic') ? btnActive : btn} onClick={() => editor?.chain().focus().toggleItalic().run()} title="이탤릭체"><i>I</i></button>
        <button className={editor?.isActive('strike') ? btnActive : btn} onClick={() => editor?.chain().focus().toggleStrike().run()} title="취소선"><s>S</s></button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 제목 */}
        <button className={editor?.isActive('heading', { level: 1 }) ? btnActive : btn} onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
        <button className={editor?.isActive('heading', { level: 2 }) ? btnActive : btn} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
        <button
          className={editor?.isActive('heading', { class: 'title-line' }) ? btnActive : btn}
          onClick={toggleTitleLine}
          title="제목선 (H1/H2 선택 후 적용)"
        >| 제목선</button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 목록/블록 */}
        <button className={editor?.isActive('bulletList') ? btnActive : btn} onClick={() => editor?.chain().focus().toggleBulletList().run()}>• 목록</button>
        <button className={editor?.isActive('blockquote') ? btnActive : btn} onClick={() => editor?.chain().focus().toggleBlockquote().run()}>□ 글상자</button>
        <button className={btn} onClick={() => editor?.chain().focus().setHorizontalRule().run()}>─ 구분선</button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 정렬 */}
        <button className={editor?.isActive({ textAlign: 'left' }) ? btnActive : btn} onClick={() => editor?.chain().focus().setTextAlign('left').run()} title="왼쪽 정렬">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="3" y="10" width="12" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="3" y="20" width="12" height="2"/></svg>
        </button>
        <button className={editor?.isActive({ textAlign: 'center' }) ? btnActive : btn} onClick={() => editor?.chain().focus().setTextAlign('center').run()} title="가운데 정렬">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="6" y="10" width="12" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="6" y="20" width="12" height="2"/></svg>
        </button>
        <button className={editor?.isActive({ textAlign: 'right' }) ? btnActive : btn} onClick={() => editor?.chain().focus().setTextAlign('right').run()} title="오른쪽 정렬">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="9" y="10" width="12" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="9" y="20" width="12" height="2"/></svg>
        </button>
        <button className={editor?.isActive({ textAlign: 'justify' }) ? btnActive : btn} onClick={() => editor?.chain().focus().setTextAlign('justify').run()} title="양쪽 정렬">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="3" y="10" width="18" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="3" y="20" width="18" height="2"/></svg>
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 표 */}
        <button className={btn} onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="표 삽입">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}>
            <rect x="3" y="3" width="18" height="18" rx="1"/>
            <line x1="3" y1="9" x2="21" y2="9"/>
            <line x1="3" y1="15" x2="21" y2="15"/>
            <line x1="9" y1="3" x2="9" y2="21"/>
            <line x1="15" y1="3" x2="15" y2="21"/>
          </svg>
        </button>
        {editor?.isActive('table') && (
          <>
            <button className={btn} onClick={() => editor.chain().focus().addColumnAfter().run()}>열+</button>
            <button className={btn} onClick={() => editor.chain().focus().deleteColumn().run()}>열-</button>
            <button className={btn} onClick={() => editor.chain().focus().addRowAfter().run()}>행+</button>
            <button className={btn} onClick={() => editor.chain().focus().deleteRow().run()}>행-</button>
            <button className={`${btn} text-red-400`} onClick={() => editor.chain().focus().deleteTable().run()}>표삭제</button>
          </>
        )}

        <div className="w-px bg-gray-300 mx-1" />

        {/* 이미지 */}
        {hasImage && (
          <button className={`${btn} flex items-center gap-1`} onClick={handleImageUpload} title="이미지 삽입">
            <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}>
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            이미지
          </button>
        )}
      </div>

      {/* 에디터 본문 */}
      <div
        ref={editorWrapRef}
        className="relative border border-t-0 border-gray-200 rounded-b-[10px] min-h-[400px] cursor-text overflow-hidden"
        onClick={() => editor?.commands.focus()}
      >
        <style>{`
          .tiptap h1 { font-size: 2em; font-weight: bold; margin: 0.5em 0; }
          .tiptap h2 { font-size: 1.5em; font-weight: bold; margin: 0.5em 0; }
          .tiptap h1.title-line, .tiptap h2.title-line { border-left: 4px solid #887267; padding-left: 10px; }
          .tiptap ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; overflow: hidden; }
          .tiptap ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
          .tiptap p { margin: 0.3em 0; line-height: 1.8; }
          .tiptap td p, .tiptap th p { margin: 0; }
          .tiptap img { max-width: 100%; height: auto; cursor: pointer; }
          .tiptap .tiptap-img { max-width: 60%; display: block; }
          .tiptap hr { border: none; border-top: 1px solid #ccc; margin: 1em 0; }
          .tiptap blockquote { border: 1px solid #ddd; border-left: 1px solid #ddd; border-radius: 10px; padding: 12px 16px; margin: 0.8em 0; background: transparent; overflow: hidden; }
          .tiptap blockquote p { margin: 0; }
          .tiptap:focus { outline: none; }
          .tiptap::after { content: ''; display: table; clear: both; }
          .tiptap table { border-collapse: collapse; width: 100%; margin: 0.8em 0; table-layout: fixed; }
          .tiptap .tableWrapper { overflow: hidden; }
          .tiptap table td, .tiptap table th { border: 1px solid #ddd; padding: 8px 12px; min-width: 60px; vertical-align: top; word-break: break-word; }
          .tiptap table th { background: #f5f5f5; font-weight: bold; }
          .tiptap table .selectedCell { background: #e8f4ff; }
          .tiptap .column-resize-handle { background-color: #887267; width: 2px; cursor: col-resize; }
          .tiptap.resize-cursor { cursor: col-resize; }
        `}</style>
        <EditorContent editor={editor} className="p-3" />
      </div>

      {/* 이미지 정렬 팝업 */}
      {imagePopup && (
        <div
          className="absolute flex gap-1 bg-white border border-gray-200 rounded-lg shadow-lg px-2 py-1 -translate-x-1/2 -translate-y-full"
          style={{ left: imagePopup.x, top: imagePopup.y, zIndex: 9999 }}
          onClick={e => e.stopPropagation()}
        >
          <select
            className="px-1 py-0.5 text-[11px] border border-gray-200 rounded bg-white cursor-pointer"
            value={imagePopup.size}
            onChange={(e) => { e.stopPropagation(); if (e.target.value) setImageSize(e.target.value) }}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">크기</option>
            {IMAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <div className="w-px bg-gray-200 mx-0.5" />
          <button className="p-1 hover:bg-gray-100 rounded text-[11px]" onClick={(e) => { e.stopPropagation(); setImageAlign('float-left') }} title="텍스트 왼쪽 배치">↰글</button>
          <button className="p-1 hover:bg-gray-100 rounded text-[11px]" onClick={(e) => { e.stopPropagation(); setImageAlign('float-right') }} title="텍스트 오른쪽 배치">글↱</button>
          <div className="w-px bg-gray-200 mx-0.5" />
          <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => { e.stopPropagation(); setImageAlign('left') }} title="왼쪽 정렬">
            <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="3" y="10" width="12" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="3" y="20" width="12" height="2"/></svg>
          </button>
          <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => { e.stopPropagation(); setImageAlign('center') }} title="가운데 정렬">
            <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="6" y="10" width="12" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="6" y="20" width="12" height="2"/></svg>
          </button>
          <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => { e.stopPropagation(); setImageAlign('right') }} title="오른쪽 정렬">
            <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor"><rect x="3" y="5" width="18" height="2"/><rect x="9" y="10" width="12" height="2"/><rect x="3" y="15" width="18" height="2"/><rect x="9" y="20" width="12" height="2"/></svg>
          </button>
        </div>
      )}
    </div>
  )
}